/**
 * Versioned HTTP contract — `v1` — for text EMBEDDING (`POST /v1/embed`, DEVOP-33 / ADR 0086).
 *
 * The gateway brokers the embedding-provider call (Voyage by default) behind the same discipline
 * as every other model route: the browser/worker sends text(s) to embed, the gateway returns the
 * vectors + the model that produced them, and usage/cost rides in the envelope `meta` (the cost
 * ledger records it, input-token-priced). The embedding MODEL is server-configured; a caller may
 * name one but the gateway validates fail-closed against its `EMBEDDING_CATALOG`.
 */
import { z } from 'zod';
import { apiEnvelope } from './v1.js';
import { EmbeddingVectorSchema } from '../evidence-embedding.js';

/** Max texts embedded in one request (a chunked source fans out; bound the batch). */
export const EMBED_BATCH_MAX = 128;
/** Max chars per input text — matches the chunk ceiling so a chunk always fits one input. */
export const EMBED_INPUT_MAX_CHARS = 8_000;

/**
 * Request body for `POST /v1/embed`: one or more texts to embed, in order. `model` is optional —
 * absent means the gateway's configured default embedding model (Voyage `voyage-3.5-lite`). The
 * response's `vectors` are parallel to `input` (same order, same length).
 */
export const EmbedRequestSchema = z.object({
  input: z.array(z.string().min(1).max(EMBED_INPUT_MAX_CHARS)).min(1).max(EMBED_BATCH_MAX),
  /** An embedding model id from the server catalog (e.g. `voyage-3.5-lite`). Never free-form. */
  model: z.string().min(1).max(120).optional(),
});
export type EmbedRequest = z.infer<typeof EmbedRequestSchema>;

/**
 * Success payload for `POST /v1/embed`: the vectors (parallel to `input`), the model that produced
 * them (lineage), and their fixed dimension. Token/cost usage rides in the envelope `meta.usage`.
 */
export const EmbedDataSchema = z.object({
  vectors: z.array(EmbeddingVectorSchema).min(1).max(EMBED_BATCH_MAX),
  /** The embedding model that answered (e.g. `voyage-3.5-lite` or `stub`). */
  model: z.string().min(1).max(120),
  /** Output vector length — every entry in `vectors` has this length. */
  dimensions: z.number().int().positive(),
});
export type EmbedData = z.infer<typeof EmbedDataSchema>;

/** Full `POST /v1/embed` response envelope. */
export const EmbedResponseSchema = apiEnvelope(EmbedDataSchema);
export type EmbedResponse = z.infer<typeof EmbedResponseSchema>;
